/**
 * CAN A COURIER BE SENT TO THIS SHOP - one answer per shop, derived once.
 *
 * A shop that lacks what a delivery provider needs to collect from it is
 * UNAVAILABLE FOR SERVICE (founder, 2026-10-07): allocation does not offer it
 * to a guest (`PICKUP_INCOMPLETE`, ALC-002), its counter is closed with the
 * same word, and Internal shows it `Incomplete` with the missing fields beside
 * the pill (OPS-003, BAK-035).
 *
 * Why it exists: nothing used to ask before the bakery accepted. Order HZFAB2
 * on demo (2026-10-06) was placed, accepted and baked at Maison Kayser Bac,
 * then refused at booking with `CONTACT_INCOMPLETE` because the shop had no
 * contact mobile, and ended NO_COURIER when its window ran out. The courier
 * adapters were right to refuse - a courier at a door with nobody to call is
 * the failure that check prevents - but by then the guest had paid and the
 * baker had baked for nothing.
 *
 * THIS MIRRORS THE ADAPTERS' REFUSAL EXACTLY AND NOTHING MORE. The pickup point
 * is `deliveryPointOf(location)` (packages/services/src/usecases/travel.ts):
 * the shop's mobile as the phone, its name as the contact, line 1 + postal code
 * + city as the address, its coordinates. `assertPointReachable`
 * (packages/provider-transport/src/limits.ts) refuses that point on an empty
 * address, a coordinate that is not finite or out of range, or an empty contact
 * name or phone. The name is `min(1)` on the wire and never empty, so three
 * requirements remain. Change one side and the other in the same change:
 * packages/services/src/usecases/pickup-readiness.test.ts holds them to each
 * other, and a requirement invented here that the adapter does not have would
 * take a shop out of service for nothing.
 *
 * Pure and I/O-free, over wire-shaped fields, so the worker, allocation's two
 * snapshot builders and the Internal screen share one implementation.
 */

/** What a shop can lack, in the order a screen names them. */
export const PICKUP_REQUIREMENTS = ['CONTACT_MOBILE', 'ADDRESS', 'COORDINATES'] as const;
export type PickupRequirement = (typeof PICKUP_REQUIREMENTS)[number];

export interface PickupReadiness {
  readonly ready: boolean;
  /** In `PICKUP_REQUIREMENTS` order; empty exactly when `ready`. */
  readonly missing: readonly PickupRequirement[];
}

export interface PickupShopFacts {
  readonly contactMobile: string | null | undefined;
  readonly addressLine1: string;
  readonly postalCode: string;
  readonly city: string;
  readonly latitude: number;
  readonly longitude: number;
}

const blank = (value: string | null | undefined): boolean => (value ?? '').trim() === '';

const outOfRange = (value: number, limit: number): boolean =>
  !Number.isFinite(value) || value < -limit || value > limit;

export function pickupReadinessOf(shop: PickupShopFacts): PickupReadiness {
  const lacks: Record<PickupRequirement, boolean> = {
    CONTACT_MOBILE: blank(shop.contactMobile),
    ADDRESS: blank(shop.addressLine1) || blank(shop.postalCode) || blank(shop.city),
    COORDINATES: outOfRange(shop.latitude, 90) || outOfRange(shop.longitude, 180),
  };
  const missing = PICKUP_REQUIREMENTS.filter((requirement) => lacks[requirement]);
  return { ready: missing.length === 0, missing };
}
